import { useState, useEffect, useCallback, useRef } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { router, useLocalSearchParams } from "expo-router";
import { IUnifiedFilter } from "common247";
import { createDefaultFilter } from "@/services/unified-filter-service";
import {
  parseFilterFromUrlParam,
  serializeFilterToUrlParam,
} from "@/utils/url-list-settings";

const FILTER_STORAGE_PREFIX = "unified_filter_";

export interface IUseFilterPersistenceResult {
  /** Active unified filter */
  filter: IUnifiedFilter;
  /** Replace the entire unified filter */
  setFilter: (newFilter: IUnifiedFilter) => void;
  /** Partially update the filter */
  updateFilter: (partial: Partial<IUnifiedFilter>) => void;
  /** Reset filter back to default empty state */
  resetFilter: () => void;
  /** Flag indicating whether the initial stored filter has loaded */
  isLoaded: boolean;
}

/**
 * Hook to persist and rehydrate unified filter settings in AsyncStorage and URL query parameters.
 * Supports deep-linking, shareable URLs, and browser back/forward navigation.
 * URL parameters take immediate precedence over local AsyncStorage on initial mount.
 *
 * @param {string} screenKey - Unique identifier for the screen (e.g. "memories", "media", "contacts")
 * @param {IUnifiedFilter} [initialOverride] - Optional default criteria override
 * @returns {IUseFilterPersistenceResult}
 */
export function useFilterPersistence(
  screenKey: string,
  initialOverride?: IUnifiedFilter
): IUseFilterPersistenceResult {
  const params = useLocalSearchParams<{ filter?: string; q?: string }>();
  const [filter, setFilterState] = useState<IUnifiedFilter>(() => {
    const base = initialOverride || createDefaultFilter();
    // Check initial URL parameters synchronously
    if (params.filter || params.q) {
      const urlCriteria = parseFilterFromUrlParam(params.filter) || {};
      const qVal = typeof params.q === "string" && params.q.trim().length > 0 ? params.q.trim() : undefined;
      return {
        ...base,
        ...urlCriteria,
        searchQuery: qVal !== undefined ? qVal : urlCriteria.searchQuery || base.searchQuery,
      };
    }
    return base;
  });

  const [isLoaded, setIsLoaded] = useState(false);
  const storageKey = `${FILTER_STORAGE_PREFIX}${screenKey}`;
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInternalUpdateRef = useRef(false);
  const lastSerializedUrlRef = useRef<string | undefined>(params.filter);

  // Rehydrate on mount (URL params take precedence over AsyncStorage)
  useEffect(() => {
    let isMounted = true;
    async function loadStoredFilter(): Promise<void> {
      try {
        // If URL parameters are already provided, respect URL precedence
        if (params.filter || params.q) {
          const urlCriteria = parseFilterFromUrlParam(params.filter) || {};
          const qVal = typeof params.q === "string" && params.q.trim().length > 0 ? params.q.trim() : undefined;
          if (isMounted) {
            setFilterState((prev) => ({
              ...createDefaultFilter(),
              ...prev,
              ...urlCriteria,
              searchQuery: qVal !== undefined ? qVal : urlCriteria.searchQuery || prev.searchQuery,
            }));
          }
          return;
        }

        const stored = await AsyncStorage.getItem(storageKey);
        if (stored && isMounted) {
          const parsed = JSON.parse(stored) as IUnifiedFilter;
          setFilterState((prev) => ({
            ...createDefaultFilter(),
            ...parsed,
          }));
        }
      } catch (err) {
        console.warn(`[useFilterPersistence] Failed to load filter for ${screenKey}:`, err);
      } finally {
        if (isMounted) setIsLoaded(true);
      }
    }

    loadStoredFilter();
    return () => {
      isMounted = false;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [storageKey, screenKey]);

  // Synchronize with external URL changes (e.g. browser back/forward)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    const currentUrlFilter = params.filter;
    const currentUrlQ = params.q;

    if (currentUrlFilter !== lastSerializedUrlRef.current) {
      lastSerializedUrlRef.current = currentUrlFilter;
      const urlCriteria = parseFilterFromUrlParam(currentUrlFilter) || {};
      const qVal = typeof currentUrlQ === "string" && currentUrlQ.trim().length > 0 ? currentUrlQ.trim() : undefined;

      setFilterState((prev) => ({
        ...createDefaultFilter(),
        ...urlCriteria,
        searchQuery: qVal !== undefined ? qVal : urlCriteria.searchQuery || "",
      }));
    }
  }, [params.filter, params.q]);

  // Persist filter on changes to AsyncStorage and URL parameters
  const persistFilter = useCallback(
    (newFilter: IUnifiedFilter) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await AsyncStorage.setItem(storageKey, JSON.stringify(newFilter));

          // Sync to URL parameters
          const serialized = serializeFilterToUrlParam(newFilter);
          const qVal = newFilter.searchQuery && newFilter.searchQuery.trim().length > 0
            ? newFilter.searchQuery.trim()
            : undefined;

          lastSerializedUrlRef.current = serialized;
          isInternalUpdateRef.current = true;

          router.setParams({
            filter: serialized,
            q: qVal,
          });
        } catch (err) {
          console.warn(`[useFilterPersistence] Failed to persist filter for ${screenKey}:`, err);
        }
      }, 250);
    },
    [storageKey, screenKey]
  );

  const setFilter = useCallback(
    (newFilter: IUnifiedFilter) => {
      setFilterState(newFilter);
      persistFilter(newFilter);
    },
    [persistFilter]
  );

  const updateFilter = useCallback(
    (partial: Partial<IUnifiedFilter>) => {
      setFilterState((prev) => {
        const updated: IUnifiedFilter = {
          ...prev,
          ...partial,
        };
        persistFilter(updated);
        return updated;
      });
    },
    [persistFilter]
  );

  const resetFilter = useCallback(() => {
    const defaultVal = createDefaultFilter();
    setFilterState(defaultVal);
    persistFilter(defaultVal);
  }, [persistFilter]);

  return {
    filter,
    setFilter,
    updateFilter,
    resetFilter,
    isLoaded,
  };
}

